import * as React from 'react';

// components
import { BetslipContext } from '../betslip';
import { StakeInput } from './stake-input';
import OCButton from '../../button';
import FormatCurrency from '~/components/format-currency';
import BookieLogo, { LOGO_SIZE } from '../../bookie-logo/bookie-logo';
import ClickoutBetslip from '../icons/clickout';

// providers
import { useServicesContext } from '~/providers/services-context';

// services
import BettingActions from '~/services/actions/betting-actions';
import {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS } from
'~/services/actions/tracking-actions';

// interfaces
import type IBookieOdds from '../interfaces/IOdds';

// styles
import styles from '../style/bookie-selection.module.css';
import { comp, formatBookmakerStyles } from '../util/utils';

interface Props {
  betIds: number[];
  changeSelection?: (bookieCode: string) => void;
  selection: IBookieOdds;
  selected?: boolean;
  stake: number;
}

const BookieSelection: React.FC<Props> = ({
  betIds,
  selection,
  selected,
  stake
}): JSX.Element => {
  const {
    labelActions,
    trackingActions,
    siteConfig: { bookmakers }
  } = useServicesContext();

  const bettingActions = new BettingActions();
  const { handleClickOut, selectBookie } = React.useContext(BetslipContext);

  const returns =
  selection && selection.oddsDecimal * stake > 100 ?
  (selection.oddsDecimal * stake).toFixed(0) :
  (selection.oddsDecimal * stake).toFixed(2);

  function renderStake() {
    if (selected) {
      return (
        <StakeInput
          stake={stake}
          returns={<FormatCurrency value={returns} />} />);


    }

    return (
      <>
				<div className={styles.sectionWrapper}>
					<div className={styles.title}>{labelActions.get('STAKE')}:</div>
					<div className={styles.number}>
						<FormatCurrency value={stake} />
					</div>
				</div>
				<div className={styles.sectionWrapper}>
					<div className={styles.title}>{labelActions.get('RETURNS')}:</div>
					<div className={styles.number}>
						<FormatCurrency value={returns} />
					</div>
				</div>
			</>);

  }

  const handleClick = (): void => {
    trackingActions.eventTracking({
      event: TRACKING_EVENTS.BET_SLIP_SIGN_UP,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_SIGN_UP],
      eventLabel: selection.bookmakerCode
    });
    handleClickOut(selection.bookmakerCode, betIds);
  };

  function renderButton(): JSX.Element {
    const { brandColour = '#000', textColour = 'light' } =
    bookmakers?.[`${selection.bookmakerCode}`];

    const bookmakerStyles = formatBookmakerStyles(brandColour, textColour);

    if (selected) {
      return (
        <OCButton
          className={styles.button}
          disabled={!stake}
          onClick={handleClick}
          style={bookmakerStyles}>

					<ClickoutBetslip />
					{labelActions.get('BET')}
				</OCButton>);

    }

    return (
      <OCButton className={styles.signUpButton} onClick={handleClick}>
				<ClickoutBetslip />
				{labelActions.get('SIGN_UP')}
			</OCButton>);

  }

  const handleBookieSelectionClick = () => {
    if (!selected && selection.index) selectBookie(selection.index);
  };

  return (
    <div
      onClick={handleBookieSelectionClick}
      role="button"
      className={comp(styles.bookieSelection, selected ? styles.selected : '')}>

			<BookieLogo
        bookieCode={selection.bookmakerCode}
        size={LOGO_SIZE.SMALL}
        className={styles.bookieLogo} />

			<div className={styles.sectionWrapper}>
				<div className={styles.title}>{labelActions.get('ODDS')}:</div>
				<div className={styles.odds}>{bettingActions.getOdds(selection)}</div>
			</div>
			{renderStake()}
			{renderButton()}
		</div>);

};

export default BookieSelection;